import type { UserManagerSettings } from 'oidc-client-ts';

const region = import.meta.env.VITE_AWS_REGION as string;
const userPoolId = import.meta.env.VITE_USER_POOL_ID as string;
const clientId = import.meta.env.VITE_USER_POOL_CLIENT_ID as string;
const cognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN as string;
const entraIssuerUrl = import.meta.env.VITE_ENTRA_ISSUER_URL as string | undefined;
const entraClientId = import.meta.env.VITE_ENTRA_CLIENT_ID as string | undefined;

// IMPORTANT: do NOT set `metadataUrl: https://<hosted-ui-domain>/.well-known/openid-configuration`.
// The Cognito Hosted UI domain (e.g. <app>-<env>.auth.<region>.amazoncognito.com) only serves
// the login UI — it does NOT expose the OIDC discovery document. Setting metadataUrl to that
// host causes a silent 404, oidc-client-ts hangs on signinRedirect, and the SPA shows a
// perpetual loading spinner. Set `authority` (the cognito-idp URL) only — oidc-client-ts will
// fetch metadata from `<authority>/.well-known/openid-configuration` automatically.
// Cognito matches sign-out/redirect URLs EXACTLY, and the app client registers
// the origin WITH a trailing slash (e.g. https://…cloudfront.net/). window.location.origin
// has no trailing slash, so send it with one appended or Cognito's /logout returns
// "An error was encountered with the requested page." (Callback keeps its /auth/callback
// path, which already matches.)
const originSlash = `${window.location.origin}/`;

export const oidcConfig: UserManagerSettings = {
  authority: `https://cognito-idp.${region}.amazonaws.com/${userPoolId}`,
  client_id: clientId,
  redirect_uri: `${window.location.origin}/auth/callback`,
  post_logout_redirect_uri: originSlash,
  response_type: 'code',
  scope: 'openid email profile',
  loadUserInfo: true,
  automaticSilentRenew: true,
};

// Cognito's own /logout only clears the Cognito session — it never propagates to the
// upstream Entra IdP (Cognito has no "log out the federated OIDC IdP too" option; that
// only exists for SAML IdPs with a configured SLO endpoint). So Cognito's logout_uri
// points at /auth/post-logout, a hand-off page that then redirects to Entra's own
// end_session_endpoint (see entraLogoutUrl below) — otherwise Entra's SSO cookie stays
// alive and the very next signinRedirect() silently re-authenticates the user.
export const cognitoSignOutUrl = (idToken?: string) => {
  const params = new URLSearchParams({
    client_id: clientId,
    logout_uri: `${originSlash}auth/post-logout`,
  });
  if (idToken) params.set('id_token_hint', idToken);
  return `https://${cognitoDomain}/logout?${params.toString()}`;
};

// Derives Entra's Microsoft-identity-platform v2.0 end_session_endpoint from the same
// issuer URL used to configure the Cognito IdP (https://login.microsoftonline.com/<tenant>/v2.0).
// Falls back to the app origin if the issuer isn't wired yet in an environment, so an
// un-migrated deploy degrades to today's Cognito-only logout instead of a broken redirect.
// `client_id` is included (not just `post_logout_redirect_uri`) because Entra only honors
// the redirect if it can tie it to a specific app registration's registered redirect URIs —
// without it, some tenants ignore post_logout_redirect_uri and show a generic sign-out page.
export const entraLogoutUrl = () => {
  if (!entraIssuerUrl) {
    console.warn('VITE_ENTRA_ISSUER_URL is not set — skipping Entra SSO logout.');
    return originSlash;
  }
  const endSessionEndpoint = `${entraIssuerUrl.replace(/\/v2\.0\/?$/, '')}/oauth2/v2.0/logout`;
  const params = new URLSearchParams({ post_logout_redirect_uri: originSlash });
  if (entraClientId) params.set('client_id', entraClientId);
  return `${endSessionEndpoint}?${params.toString()}`;
};
